// Renders a blog post's body to an HTML string.
//
// Shared by the React BlogPost page (via dangerouslySetInnerHTML) and by
// prerender.cjs at build time, so crawlers and readers get identical markup
// from one source. CommonJS so the Node build script can require() it.
//
// Post content is our own JSON (src/blog/posts.json), not user input; every
// string is still HTML-escaped before the small inline syntax below is applied.
//
// Section blocks:
//   "plain string"                   paragraph
//   { "h3": "Subheading" }
//   { "ul": ["item", ...] }          bullets
//   { "ol": ["item", ...] }          numbered list
//   { "checklist": ["item", ...] }   tick-box list
//   { "table": { "columns": [...], "rows": [[...], ...] } }
// Inline: **bold** and [link text](/path-or-url)

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inline(text) {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, href) => {
      const external = /^https?:\/\//.test(href);
      return external
        ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
        : `<a href="${href}">${label}</a>`;
    });
}

function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/^\d+\.\s*/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Older posts used { paragraphs, bullets }; normalise them to blocks.
function sectionBlocks(section) {
  if (Array.isArray(section.blocks)) return section.blocks;
  const blocks = [...(section.paragraphs || [])];
  if (section.bullets && section.bullets.length) blocks.push({ ul: section.bullets });
  return blocks;
}

function renderBlock(block) {
  if (typeof block === 'string') return `<p>${inline(block)}</p>`;
  if (block.h3) return `<h3>${inline(block.h3)}</h3>`;
  if (block.ul) return `<ul>${block.ul.map((i) => `<li>${inline(i)}</li>`).join('')}</ul>`;
  if (block.ol) return `<ol>${block.ol.map((i) => `<li>${inline(i)}</li>`).join('')}</ol>`;
  if (block.checklist) {
    return `<ul class="blog-checklist">${block.checklist.map((i) => `<li>${inline(i)}</li>`).join('')}</ul>`;
  }
  if (block.table) {
    const { columns, rows } = block.table;
    const head = `<thead><tr>${columns.map((c) => `<th scope="col">${inline(c)}</th>`).join('')}</tr></thead>`;
    const body = `<tbody>${rows
      .map((r) => `<tr>${r.map((cell, i) => (i === 0 ? `<th scope="row">${inline(cell)}</th>` : `<td>${inline(cell)}</td>`)).join('')}</tr>`)
      .join('')}</tbody>`;
    // the wrapper scrolls sideways on narrow screens instead of squashing columns
    return `<div class="blog-table-wrap" tabindex="0"><table>${head}${body}</table></div>`;
  }
  return '';
}

function renderPostBody(post) {
  const content = post.content || {};
  const sections = content.sections || [];
  const parts = [];

  (content.intro || []).forEach((p) => parts.push(`<p class="blog-lede">${inline(p)}</p>`));

  if (content.toc && sections.length) {
    parts.push(
      `<nav class="blog-toc" aria-label="In this guide"><p class="blog-toc-title">In this guide</p><ol>${sections
        .map((s) => `<li><a href="#${slugify(s.heading)}">${inline(s.heading.replace(/^\d+\.\s*/, ''))}</a></li>`)
        .join('')}</ol></nav>`
    );
  }

  sections.forEach((s) => {
    parts.push(
      `<section class="blog-section" id="${slugify(s.heading)}"><h2>${inline(s.heading)}</h2>${sectionBlocks(s)
        .map(renderBlock)
        .join('')}</section>`
    );
  });

  if (content.outroHeading) parts.push(`<h2>${inline(content.outroHeading)}</h2>`);
  (content.outro || []).forEach((p) => parts.push(`<p>${inline(p)}</p>`));

  if (content.cta && content.cta.href && content.cta.label) {
    parts.push(`<p class="blog-cta"><a class="blog-cta-link" data-track="blog_post" href="${content.cta.href}">${escapeHtml(content.cta.label)}</a></p>`);
  }

  return parts.join('\n');
}

// Plain text of a post, for reading-time estimates.
function postText(post) {
  const content = post.content || {};
  const flat = (b) => {
    if (typeof b === 'string') return b;
    if (b.h3) return b.h3;
    const list = b.ul || b.ol || b.checklist;
    if (list) return list.join(' ');
    if (b.table) return [...b.table.columns, ...b.table.rows.flat()].join(' ');
    return '';
  };
  return [
    ...(content.intro || []),
    ...(content.sections || []).flatMap((s) => [s.heading, ...sectionBlocks(s).map(flat)]),
    ...(content.outro || []),
  ].join(' ');
}

module.exports = { renderPostBody, postText, escapeHtml };
